import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaFileAlt, FaBox, FaQuestion, FaClipboardList, FaSpinner, FaArrowLeft, FaDesktop, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import Header from './Header';
import { synthesizeIdeas } from '../utils/openai';
import './SynthesisOptions.css';

interface Slide {
  title: string;
  content: string[];
  notes?: string;
  visuals?: string;
}

interface SynthesisResult {
  loading: boolean;
  content: string | null;
  error: string | null;
  slides?: Slide[];
  currentSlide?: number;
}

interface IdeaContent {
  id: string;
  content: string;
}

const SynthesisOptions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [result, setResult] = useState<SynthesisResult>({
    loading: false,
    content: null,
    error: null,
    slides: [],
    currentSlide: 0
  });
  const [loadingOption, setLoadingOption] = useState<string | null>(null);

  const selectedIdeasContent = location.state?.selectedIdeasContent || [];

  const handleBack = () => {
    navigate(-1);
  };

  const options = [
    {
      id: 'research',
      title: 'Research Paper',
      icon: <FaFileAlt />,
      description: 'Transform ideas into an academic research paper format'
    },
    {
      id: 'product',
      title: 'Product Document',
      icon: <FaBox />,
      description: 'Create a comprehensive product documentation'
    },
    {
      id: 'prfaq',
      title: 'PRFAQ',
      icon: <FaQuestion />,
      description: 'Generate a Press Release and FAQ document'
    },
    {
      id: 'prd',
      title: 'PRD',
      icon: <FaClipboardList />,
      description: 'Develop a Product Requirements Document'
    },
    {
      id: 'slides',
      title: 'Slide Deck',
      description: 'Transform ideas into a compelling presentation with detailed slide outlines, speaker notes, and visual suggestions.',
      icon: <FaDesktop />
    }
  ];

  const parseMarkdownToSlides = (markdown: string): Slide[] => {
    const slides: Slide[] = [];
    const sections = markdown.split(/(?=^#\s+)/m);

    sections.forEach(section => {
      if (section.trim()) {
        const lines = section.split('\n');
        const title = lines[0].replace(/^#\s+/, '').trim()
          .replace(/^(?:Slide\s*)?(?:\d+\.?\)?:?\s*)?/, '')
          .replace(/^\([^)]+\)\s*/, '');
        
        let currentSection: 'content' | 'notes' | 'visuals' = 'content';
        const content: string[] = [];
        let notes = '';
        let visuals = '';
        let currentContent = '';

        lines.slice(1).forEach(line => {
          const trimmedLine = line.trim().toLowerCase();
          
          if (trimmedLine === 'speaker notes:' || trimmedLine === 'notes:') {
            if (currentContent) {
              content.push(currentContent
                .replace(/^\d+\.\s+/gm, '• ')
                .replace(/^-\s+/gm, '• ')
                .trim());
              currentContent = '';
            }
            currentSection = 'notes';
            return;
          }
          
          if (trimmedLine === 'suggested visuals:' || trimmedLine === 'visuals:') {
            if (currentContent) {
              content.push(currentContent
                .replace(/^\d+\.\s+/gm, '• ')
                .replace(/^-\s+/gm, '• ')
                .trim());
              currentContent = '';
            }
            currentSection = 'visuals';
            return;
          }

          if (line.trim()) {
            switch (currentSection) {
              case 'content':
                currentContent += line + '\n';
                break;
              case 'notes':
                notes += line.replace(/^\d+\.\s+/, '') + '\n';
                break;
              case 'visuals':
                visuals += line.replace(/^\d+\.\s+/, '') + '\n';
                break;
            }
          }
        });

        if (currentContent) {
          content.push(currentContent
            .replace(/^\d+\.\s+/gm, '• ')
            .replace(/^-\s+/gm, '• ')
            .trim());
        }

        if (title) {
          slides.push({
            title,
            content,
            notes: notes.trim(),
            visuals: visuals.trim()
          });
        }
      }
    });

    return slides;
  };

  const nextSlide = () => {
    if (result.slides && result.currentSlide! < result.slides.length - 1) {
      setResult(prev => ({ ...prev, currentSlide: prev.currentSlide! + 1 }));
    }
  };

  const previousSlide = () => {
    if (result.currentSlide! > 0) {
      setResult(prev => ({ ...prev, currentSlide: prev.currentSlide! - 1 }));
    }
  };

  const handleOptionSelect = async (optionId: 'research' | 'product' | 'prfaq' | 'prd' | 'slides') => {
    setResult({ loading: true, content: null, error: null, slides: [], currentSlide: 0 });
    setLoadingOption(optionId);
    
    try {
      const ideasForSynthesis = selectedIdeasContent.map((idea: IdeaContent) => ({
        content: idea.content,
        parentChain: []
      }));

      const content = await synthesizeIdeas(ideasForSynthesis, optionId);
      
      if (optionId === 'slides') {
        const slides = parseMarkdownToSlides(content);
        setResult({
          loading: false,
          content,
          error: null,
          slides,
          currentSlide: 0
        });
      } else {
        setResult({
          loading: false,
          content,
          error: null
        });
      }
    } catch (error) {
      console.error('Synthesis error:', error);
      setResult({
        loading: false,
        content: null,
        error: 'Failed to synthesize ideas. Please try again.',
        slides: [],
        currentSlide: 0
      });
    } finally {
      setLoadingOption(null);
    }
  };

  return (
    <>
      <Header />
      <div className="sidebar">
        <div className="sidebar-buttons">
          <button 
            onClick={handleBack}
            className="sidebar-button"
            data-tooltip-id="main-tooltip"
            data-tooltip-content="Go back"
          >
            <FaArrowLeft />
            <span>Back</span>
          </button>
        </div>
      </div>
      <div className="synthesis-container">
        <div className="synthesis-content">
          {!result.content ? (
            <>
              <h2>Choose Synthesis Format</h2>
              <div className="synthesis-options">
                {options.map(option => (
                  <button
                    key={option.id}
                    className="synthesis-option"
                    onClick={() => handleOptionSelect(option.id as 'research' | 'product' | 'prfaq' | 'prd' | 'slides')}
                    disabled={loadingOption !== null}
                  >
                    <div className="option-icon">
                      {loadingOption === option.id ? <FaSpinner className="spinner" /> : option.icon}
                    </div>
                    <h3>{option.title}</h3>
                    <p>{option.description}</p>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="synthesis-result">
              {result.slides && result.slides.length > 0 ? (
                <div className="slide-deck">
                  <div className="slide-content">
                    <h1>{result.slides[result.currentSlide!].title}</h1>
                    <div className="slide-body">
                      {result.slides[result.currentSlide!].content.map((point, index) => (
                        <ReactMarkdown 
                          key={index}
                          components={{
                            ul: ({children}) => <div className="slide-bullets">{children}</div>,
                            li: ({children}) => <div className="slide-bullet">{children}</div>
                          }}
                        >
                          {point}
                        </ReactMarkdown>
                      ))}
                    </div>
                  </div>
                  <div className="slide-controls">
                    <button 
                      onClick={previousSlide}
                      disabled={result.currentSlide === 0}
                    >
                      <FaChevronLeft /> Previous
                    </button>
                    <span>{result.currentSlide! + 1} / {result.slides.length}</span>
                    <button 
                      onClick={nextSlide}
                      disabled={result.currentSlide === result.slides.length - 1}
                    >
                      Next <FaChevronRight />
                    </button>
                  </div>
                  {result.slides[result.currentSlide!].notes && (
                    <div className="speaker-notes">
                      <h4>Speaker Notes</h4>
                      <p>{result.slides[result.currentSlide!].notes}</p>
                    </div>
                  )}
                  {result.slides[result.currentSlide!].visuals && (
                    <div className="visual-suggestions">
                      <h4>Suggested Visuals</h4>
                      <p>{result.slides[result.currentSlide!].visuals}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="result-content">
                  <ReactMarkdown>{result.content}</ReactMarkdown>
                </div>
              )}
            </div>
          )}
          
          {result.error && (
            <div className="synthesis-error">
              {result.error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SynthesisOptions; 